import { useCallback } from 'react'

import {
  StatusCheckCategoryFieldVisibilityState,
  useGetCategoryFieldVisibilityState
} from 'components/status-checks/hooks/use-get-category-field-visibility-state'
import {
  isDynatraceSelectorEntityIdType,
  isDynatraceSelectorK8sQueryType
} from 'components/status-checks/status-checks-dynatrace-form-utils'
import {
  buildStatusCheckFormEvaluationConfiguration,
  buildStatusCheckTeamExternalIntegration,
  buildTeamExternalIntegration,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import useAuth from 'hooks/use-auth'
import {
  type DynatraceStatusCheckCreateRequest,
  ObservabilityToolType,
  type StatusCheckCreateRequest
} from 'types/api/status-checks/status-check'
import {
  getEndpointConfigurationUrl,
  encodeQueryParams
} from 'utils/team-external-integrations/team-external-integrations-util'

/**
 * Returns a function which will build the Status Check form request payload based on
 * the provided form values. Manages conditional logic determining which fields to include.
 */
export function useStatusCheckRequestBuilder() {
  const { currentTeamId } = useAuth()

  const getCategoryFieldVisibilityState = useGetCategoryFieldVisibilityState()

  return useCallback(
    async (
      formValues: StatusCheckFormValues
    ): Promise<
      StatusCheckCreateRequest | DynatraceStatusCheckCreateRequest
    > => {
      const {
        dynatraceSelector,
        isPrivateNetworkIntegration,
        observabilityTool,
        pollingIntervalSeconds,
        statusCheckName
      } = formValues
      const { type: dynatraceSelectorType } = dynatraceSelector ?? {}
      const { type: observabilityToolType } = observabilityTool ?? {}

      const categoryFieldVisibilityState = getCategoryFieldVisibilityState(
        observabilityTool,
        dynatraceSelectorType
      )
      const shouldIncludeCategoryField =
        categoryFieldVisibilityState !==
        StatusCheckCategoryFieldVisibilityState.HIDDEN

      const integration = buildTeamExternalIntegration(formValues)
      const isCustomObservabilityTool =
        observabilityToolType === ObservabilityToolType.CUSTOM
      const isDynatraceObservabilityTool =
        observabilityToolType === ObservabilityToolType.DYNATRACE

      const isEntityIdType = isDynatraceSelectorEntityIdType(dynatraceSelector)
      const isK8sQueryType = isDynatraceSelectorK8sQueryType(dynatraceSelector)

      // Common fields required for all known Health Check variant requests
      const sharedPayloadValues = {
        isContinuous: true,
        isPrivateNetwork: isPrivateNetworkIntegration,
        name: statusCheckName ?? '',
        pollingIntervalSeconds: parseInt(pollingIntervalSeconds, 10),
        teamExternalIntegration:
          buildStatusCheckTeamExternalIntegration(formValues),
        thirdPartyPresets: integration.type
      } as const

      /**
       * When creating a Dynatrace 'IHC', and our configuration selection option is one of the new
       * Dynatrace entity selection sources (excluding the PROBLEMS_URL), we use the targetInfo
       * directly as the request payload, and target a separate Dynatrace-specific endpoint.
       */
      if (isDynatraceObservabilityTool && (isEntityIdType || isK8sQueryType)) {
        /*
         * The Dynatrace HC endpoints build the `evaluationConfiguration` object based on the parameters
         * received from the request. The `endpointConfiguration` data is supplanted by the Dynatrace-specific
         * parameters which are used to validate whether such Problems in Dynatrace which map to this HC configuration exist.
         * Alternatively, we pass on user-supplied targeting information to create the HC if the pre-fetch endpoint
         * is unable to find a matching entity.
         */
        return {
          ...sharedPayloadValues,
          // Payload shape depends on the current selector type chosen
          ...(isEntityIdType
            ? { entityId: dynatraceSelector.entityId }
            : { k8sQuery: dynatraceSelector.k8sQuery }),
          // 'category' field is always required for our enhanced Dynatrace HCs
          category: formValues.category
        }
      }

      const { method, headers, payload } =
        formValues.endpointConfiguration ?? {}

      /*
       * Type-safe construction of additional endpoint configuration fields.
       * Applies to Custom & Prometheus observability tools only.
       */
      const customObservabilityToolEndpointConfig = !isCustomObservabilityTool
        ? undefined
        : method === 'POST'
        ? { method, headers, payload }
        : { method }

      return {
        ...sharedPayloadValues,
        ...(shouldIncludeCategoryField && { category: formValues.category }),
        endpointConfiguration: {
          ...{
            ...customObservabilityToolEndpointConfig,
            url: encodeQueryParams(
              await getEndpointConfigurationUrl(integration, currentTeamId)
            )
          }
        },
        evaluationConfiguration:
          await buildStatusCheckFormEvaluationConfiguration({
            currentTeamId,
            formValues,
            integration
          }),
        rawEndpointConfiguration: { url: integration.url }
      }
    },
    [currentTeamId, getCategoryFieldVisibilityState]
  )
}
